import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface KeywordFilter {
  keyword?: string;
  location_name?: string;
  location_code?: number;
  language_name?: string;
  language_code?: string;
  include_seed_keyword?: boolean;
  include_serp_info?: boolean;
  include_clickstream_data?: boolean;
  exact_match?: boolean;
  ignore_synonyms?: boolean;
  filters?: any[];
  order_by?: string[];
  limit?: number;
  offset?: number;
}

@Injectable({
  providedIn: 'root',
})
export class DataForSeoService {
  private readonly baseUrl = 'https://api.dataforseo.com/v3/dataforseo_labs';
  private readonly auth: string;

  constructor(private http: HttpClient) {
    // Create base64 encoded credentials
    this.auth = btoa(
      `${environment.dataForSeo.login}:${environment.dataForSeo.password}`
    );
  }

  getKeywordSuggestions(params: KeywordFilter): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Basic ${this.auth}`,
    });

    // Build request body with all parameters
    const body = [
      {
        keyword: params.keyword,
        location_code: params.location_code || 2840, // Default to US
        language_code: params.language_code || 'en', // Default to English
        location_name: params.location_name,
        language_name: params.language_name,
        include_seed_keyword: params.include_seed_keyword ?? true,
        include_serp_info: params.include_serp_info ?? true,
        include_clickstream_data: params.include_clickstream_data ?? false,
        exact_match: params.exact_match ?? false,
        ignore_synonyms: params.ignore_synonyms ?? false,
        filters: params.filters,
        order_by: params.order_by,
        limit: params.limit || 100,
        offset: params.offset,
      },
    ];

    console.log('DataForSEO Request:', {
      url: `${this.baseUrl}/google/keyword_suggestions/live`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Basic ****', // Hide credentials
      },
      body,
    });

    return this.http
      .post(`${this.baseUrl}/google/keyword_suggestions/live`, body, {
        headers,
      })
      .pipe(
        tap((response: any) => {
          console.log('DataForSEO Raw Response:', response);
        }),
        map((response: any) => {
          if (!response?.tasks?.[0]?.result?.[0]?.items) {
            console.warn('DataForSEO Response missing items:', response);
            return [];
          }

          const items = response.tasks[0].result[0].items;
          if (!Array.isArray(items)) {
            console.warn(
              'DataForSEO Response items is not an array:',
              response
            );
            return [];
          }

          // Transform the items to match our expected format
          return items.map((item: any) => ({
            keyword: item.keyword,
            searchVolume: item.keyword_info?.search_volume || 0,
            competition: item.keyword_info?.competition || 0,
            cpc: item.keyword_info?.cpc || 0,
            categories: item.keyword_info?.categories || [],
            monthly_searches: item.keyword_info?.monthly_searches || [],
            search_intent_info: item.search_intent_info || null,
            serp_info: item.serp_info || null,
          }));
        }),
        catchError((error: any) => {
          console.error('DataForSEO API error:', error);
          if (error.error?.tasks?.[0]?.status_message) {
            console.error(
              'API Status Message:',
              error.error.tasks[0].status_message
            );
          }
          return throwError(() => error);
        })
      );
  }
}
